import { TrackingService } from './../../../shared/services/tracking/google-analytics-events.service';
import { TrackingConstants } from './../../../shared/services/tracking/tracking-constants.service';
import { Component, OnInit, Input, HostListener } from '@angular/core';
import { MdDialogRef } from '@angular/material';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { URLSearchParams } from '@angular/http';
import { Observable } from 'rxjs/Observable';

import { ArchivalConfirm } from './archival-confirm.model';
import { ArchivalConfirmService } from './archival-confirm.service';
import { compareElements } from './../../../shared/validators/generic-validators';
import { SnackBarService } from './../../../services/snackbar.service';
import { MessageService } from './../../../services/messages.service';
import { unsavedFormCheck } from './../../../shared/validators/generic-validators';
import { EntityRelationsService } from 'app/entities/relations/entity-relations.service';
import { EntityRelation } from 'app/shared/models/entity-relation.model';

@Component({
  templateUrl: './archival-confirm.component.html',
  providers: [ArchivalConfirmService],
  styleUrls: ['./archival-confirm.component.css']
})

/**
 * Dialog component to confirm archival entity deletion.
 *
 * @author mohit.khurana@vernuso.com (Mohit Khurana)
 */
export class ArchivalConfirmComponent implements OnInit {
  @Input() archivalConfirm: ArchivalConfirm;

  archivalConfirmForm: FormGroup;
  flagArchival = false;
  argMultipleArchival: boolean;
  argEntityId: number;
  argEntityValues: any[] = [];
  archivalEntitiesSubmit: any[] = [];
  argDeleteClientOnly: boolean;
  loadingRelationships = false;
  hasRelationships = false;
  relationshipWarning = '';
  relatedEntityNames: string[] = [];

  formErrors = {
    'archivalreason': '',
  };

  validationMessages = {
    'archivalreason': {
      'required': 'Required field value.',
    }
  };

  constructor(private formBuilder: FormBuilder,
    public dialogRef: MdDialogRef<any>,
    private archivalConfirmService: ArchivalConfirmService,
    private entityRelationsService: EntityRelationsService,
    private snackBarService: SnackBarService,
    private trackingService: TrackingService) {
  }

  ngOnInit(): void {
    this.buildForm();
    if (!this.argMultipleArchival && this.argEntityId) {
      this.loadRelationshipWarnings();
    }
  }

  loadRelationshipWarnings() {
    this.loadingRelationships = true;
    Observable.forkJoin(
      this.entityRelationsService.getRelationshipsWithOffsetAndLimits(this.argEntityId, 0, 100),
      this.entityRelationsService.loadReverseRelationshipDetails(this.argEntityId)
    ).subscribe(
      ([forwardResponse, reverseResponse]) => {
        const forwardRelations: EntityRelation[] = forwardResponse.arrResult.relations || [];
        const reverseRelations: EntityRelation[] = reverseResponse.arrResult || [];
        const forwardCount = forwardResponse.arrResult.totalRecords || forwardRelations.length;
        const reverseCount = reverseRelations.length;

        this.hasRelationships = forwardCount > 0 || reverseCount > 0;

        const names = new Set<string>();
        forwardRelations.forEach(relation => {
          if (relation.name) {
            names.add(relation.name);
          }
        });
        reverseRelations.forEach(relation => {
          if (relation.name) {
            names.add(relation.name);
          }
        });
        this.relatedEntityNames = Array.from(names).slice(0, 10);

        if (this.hasRelationships) {
          const parts = [];
          if (forwardCount > 0) {
            parts.push(`${forwardCount} outgoing relationship${forwardCount === 1 ? '' : 's'}`);
          }
          if (reverseCount > 0) {
            parts.push(`${reverseCount} incoming relationship${reverseCount === 1 ? '' : 's'}`);
          }
          this.relationshipWarning =
            `This entity has ${parts.join(' and ')}. Archiving will permanently remove these relationships along with the entity.`;
        }
        this.loadingRelationships = false;
      },
      () => {
        this.loadingRelationships = false;
      }
    );
  }

  buildForm() {
    this.archivalConfirmForm = this.formBuilder.group({
      archivalreason: ['', Validators.required],
    });

    this.archivalConfirmForm.valueChanges.subscribe(data => this.onValueChanged(data));
    this.onValueChanged(); // (re)set validation messages now
  }

  onValueChanged(data?: any) {
    if (!this.archivalConfirmForm) {
      return;
    }
    const form = this.archivalConfirmForm;

    for (const field in this.formErrors) {
      // clear previous error message (if any)
      this.formErrors[field] = '';
      const control = form.get(field);
      if (control && control.touched && !control.valid) {
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + ' ';
        }
      }
    }
  }

  onSubmit({ value, valid }) {
    this.flagArchival = true;
    this.archivalConfirmForm.disable();
    const archivalConfirmModel = new ArchivalConfirm();
    // This code snippet will execute when multiple entities are archived.
    if(this.argMultipleArchival) {
      this.argEntityValues.forEach((argEntityValue, index) => {
        let confirmEntityClientId = argEntityValue.entityClientId;
        let confirmEntityClientType = argEntityValue.entityClientType;
        let confirmDeleteClientOnly = false;
        // This flag will be set only if entity is an organisation and client Id is present.
        if (confirmEntityClientType && confirmEntityClientType.toLowerCase() === 'org' && confirmEntityClientId) {
            confirmDeleteClientOnly = true;
        }
        // Get the entities list data to be archived as an array of objects.
        this.archivalEntitiesSubmit.push(
        {
            'deleteReason'     : value.archivalreason,
            'deleteClientOnly' : confirmDeleteClientOnly,
            'entityId'         : argEntityValue.entityItemId
        })
      });
      if(this.archivalEntitiesSubmit.length > 0)
      {
        this.archivalConfirmService.confirmMultipleArchival(this.archivalEntitiesSubmit)
          .subscribe(response => {
            if (response.flagResult) {
              this.dialogRef.close(archivalConfirmModel);
            }
            this.trackingService
              .emitEvent(
              TrackingConstants.ENTITY_ARCHIVAL,
              TrackingConstants.PERFORM_ENTITY_ARCHIVAL,
              TrackingConstants.SUCCESS, 1);
            this.callSnackBarService(response);
          }, err => {
            this.trackingService
              .emitEvent(
              TrackingConstants.ENTITY_ARCHIVAL,
              TrackingConstants.PERFORM_ENTITY_ARCHIVAL,
              TrackingConstants.FAILURE, 0);
            this.callSnackBarService(err);
          }
        );
      }
    // This code snippet will execute when single entity is archived.
    } else {
      archivalConfirmModel.deleteReason     = value.archivalreason;
      archivalConfirmModel.entityId         = this.argEntityId;
      archivalConfirmModel.deleteClientOnly = this.argDeleteClientOnly;
      this.archivalConfirmService.confirmArchival(archivalConfirmModel)
        .subscribe(response => {
          if (response.flagResult) {
            this.dialogRef.close(archivalConfirmModel);
          }
          this.trackingService
            .emitEvent(
            TrackingConstants.ENTITY_ARCHIVAL,
            TrackingConstants.PERFORM_ENTITY_ARCHIVAL,
            TrackingConstants.SUCCESS, 1);
          this.callSnackBarService(response);
        }, err => {
          this.trackingService
            .emitEvent(
            TrackingConstants.ENTITY_ARCHIVAL,
            TrackingConstants.PERFORM_ENTITY_ARCHIVAL,
            TrackingConstants.FAILURE, 0);
          this.callSnackBarService(err);
        }
      );
    }
  }

  callSnackBarService(response: any) {
    this.snackBarService
      .showStatus(response,
      MessageService.ARCHIVAL_SUCCESS,
      MessageService.ARCHIVAL_FAILURE);
  }

  @HostListener('keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    unsavedFormCheck(event, this.dialogRef, this.archivalConfirmForm);
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/archival/archivalconfirm/archival-confirm.component.ts